/**
 * Quand faut-il réclamer la mise de côté, et quand faut-il se taire ?
 *
 * Le stock d'une commande eshop est déduit dès sa création, mais la marchandise
 * reste en rayon et se vend une seconde fois : mesuré sur la production, 21 lignes
 * réparties sur 6 des 11 commandes de l'eshop sont tombées à zéro par une vente au
 * comptoir entre la commande et sa préparation (chèvre de Gwendoline Michel,
 * 12/09/2026 — commandé à 05 h 25, vendu au comptoir, préparé à 11 h 35). La seule
 * parade est humaine : que quelqu'un aille chercher le produit. Ce hook décide du
 * moment où on le demande.
 *
 * Trois cadences, pour trois raisons différentes :
 *  - 60 s pour interroger le serveur au repos. L'eshop reçoit 1 à 3 commandes par
 *    jour et deux d'entre elles ont été préparées en 1,1 h et 3,6 h : une minute est
 *    déjà dix fois plus fin que l'événement guetté. On ne descend pas plus bas — le
 *    compteur de messagerie a coûté ~52 000 appels en 14 jours depuis les caisses
 *    (05/08/2026) et le poste travaille en 4G au marché.
 *  - 15 s tant que la fenêtre est OUVERTE : c'est le seul moment où il faut voir
 *    vite qu'un autre poste vient d'y aller, et il dure quelques minutes par jour.
 *  - 2 s pour réévaluer « est-ce le bon moment », sans aucune requête.
 *
 * La fenêtre ne s'ouvre JAMAIS par-dessus un geste de caisse : un client au comptoir
 * passe avant, et la commande attendra une minute de plus. « Panier vide » ne suffit
 * pas à le dire — le premier article d'un client se pèse avec un panier encore vide —
 * d'où la conjonction : panier vide, pas de paiement, pas de dialogue qui capte la
 * douchette, pas de pesée, et 10 s sans un doigt sur l'écran.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  acquitterMisesDeCote,
  listerMisesDeCote,
  reporterMisesDeCote,
  AcquittementRecent,
  ArticleAMettreDeCote,
  CommandeAMettreDeCote,
} from '../api/misesDeCote.api';
import { usePOSStore } from './usePOSStore';
import { logger } from '../utils/logger';

const DELAI_SONDAGE_REPOS_MS = 60_000;
const DELAI_SONDAGE_FENETRE_OUVERTE_MS = 15_000;
const DELAI_DECISION_MS = 2_000;
/** Sans un doigt sur l'écran depuis ce délai, on considère le poste disponible. */
const DELAI_CALME_MS = 10_000;
/** Borne de temps d'une requête, comme le reste du POS (services/api.ts). */
const DELAI_MAX_REQUETE_MS = 8_000;
/**
 * « Pas maintenant » : la fenêtre se tait un quart d'heure sur CE poste — elle
 * n'acquitte rien et ne dit rien aux autres. C'est la porte de sortie honnête
 * sans laquelle le seul geste possible, quand un client attend au comptoir,
 * serait de déclarer « c'est mis de côté » pour des produits que personne n'est
 * allé chercher. Un signal qu'on ne peut pas repousser finit toujours par être
 * éteint à tort.
 */
const REPOS_APRES_PLUS_TARD_MS = 15 * 60_000;

export interface AcquittementAffiche {
  par_nom: string | null;
  at: string | null;
}

export interface EtatMisesDeCote {
  commandes: CommandeAMettreDeCote[];
  /** Les articles qu'il reste à aller chercher (personne ne les a encore acquittés). */
  articlesAFaire: ArticleAMettreDeCote[];
  nombreAFaire: number;
  visible: boolean;
  enLigne: boolean;
  acquittementEnCours: boolean;
  erreur: string | null;
  /** Posé quand les articles viennent d'être mis de côté — ici ou sur un autre poste. */
  dernierAcquittement: AcquittementAffiche | null;
  ouvrir: () => void;
  /** Acquitte les lignes RÉELLEMENT affichées (jamais l'état serveur du moment). */
  acquitter: (idsLus?: string[]) => Promise<void>;
  /** Repousse la fenêtre 15 min sur ce poste, sans rien acquitter. */
  plusTard: () => void;
  /** Les lignes affichées se taisent jusqu'à demain, sur les trois caisses. */
  rappelerDemain: (idsLus: string[]) => Promise<void>;
  oublierDernierAcquittement: () => void;
}

const tousLesArticles = (commandes: CommandeAMettreDeCote[]): ArticleAMettreDeCote[] =>
  commandes.flatMap((c) => c.articles || []);

export function useMisesDeCote({ actif, enLigne }: { actif: boolean; enLigne: boolean }): EtatMisesDeCote {
  const [commandes, setCommandes] = useState<CommandeAMettreDeCote[]>([]);
  const [visible, setVisible] = useState(false);
  const [masqueeJusqua, setMasqueeJusqua] = useState<number | null>(null);
  const [acquittementEnCours, setAcquittementEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [dernierAcquittement, setDernierAcquittement] = useState<AcquittementAffiche | null>(null);

  // Le sondage et la décision d'ouverture tournent dans des intervalles : ils lisent
  // l'état par des refs, jamais par une clôture qui serait périmée au tour suivant.
  const visibleRef = useRef(false);
  visibleRef.current = visible;
  const lignesAFaireRef = useRef<Set<string>>(new Set());
  const derniereActiviteRef = useRef<number>(Date.now());

  // Activité de l'opérateur. Écouteurs passifs en phase de capture : ils ne
  // consomment aucun événement, ils datent seulement le dernier geste — y compris
  // le keydown que produit la douchette, ce qui suffit à repérer un scan en cours.
  useEffect(() => {
    const noter = () => {
      derniereActiviteRef.current = Date.now();
    };
    const evenements = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;
    evenements.forEach((e) => document.addEventListener(e, noter, { capture: true, passive: true }));
    return () => evenements.forEach((e) => document.removeEventListener(e, noter, { capture: true } as any));
  }, []);

  // `enLigne` lu par le décideur sans le faire dépendre de lui : un battement de
  // réseau ne doit pas relancer la minuterie de décision.
  const enLigneRef = useRef(enLigne);
  useEffect(() => { enLigneRef.current = enLigne; }, [enLigne]);

  // Une requête à la fois, et bornée dans le temps. Sans cela, une coupure 4G
  // silencieuse laisse la requête pendre jusqu'au délai TCP du système (~60 s)
  // pendant que le sondage en relance une chaque minute : les réponses finissent
  // par arriver toutes ensemble, dans le désordre, et la plus ancienne peut
  // réécrire la liste avec un état périmé — donc réclamer un article déjà
  // acquitté. Le reste du POS borne ses appels à 4 s pour cette raison exacte.
  const enVolRef = useRef<AbortController | null>(null);

  const charger = useCallback(async () => {
    if (enVolRef.current) return;
    const controleur = new AbortController();
    enVolRef.current = controleur;
    const minuterie = setTimeout(() => controleur.abort(), DELAI_MAX_REQUETE_MS);
    try {
      const reponse = await listerMisesDeCote(controleur.signal);
      setCommandes(reponse.commandes);

      const aFaire = tousLesArticles(reponse.commandes);
      const idsAvant = lignesAFaireRef.current;
      lignesAFaireRef.current = new Set(aFaire.map((a) => a.line_id));

      // Plus rien à faire alors que la fenêtre est ouverte : quelqu'un vient d'y
      // aller. On referme en NOMMANT qui — le serveur retire les lignes acquittées
      // de la liste, donc sans `acquittements_recents` la fenêtre s'évaporerait
      // toute seule et deux personnes iraient au frigo pour rien.
      if (visibleRef.current && aFaire.length === 0) {
        const dernier = (reponse.acquittements_recents || [])
          .filter((a: AcquittementRecent) => idsAvant.has(a.line_id))
          .sort((x, y) => String(y.mis_de_cote_at).localeCompare(String(x.mis_de_cote_at)))[0];
        setDernierAcquittement({
          par_nom: dernier?.mis_de_cote_par_nom ?? null,
          at: dernier?.mis_de_cote_at ?? null,
        });
        setVisible(false);
      }
    } catch (err) {
      // Le serveur est injoignable ou refuse : la caisse continue, on garde la
      // dernière liste connue et on ne dit rien de plus qu'on ne sait.
      logger.warn('[MisesDeCote] liste indisponible:', err);
    } finally {
      clearTimeout(minuterie);
      enVolRef.current = null;
    }
  }, []);

  // Sondage. Hors ligne : AUCUNE requête — elle échouerait, et l'acquittement ne
  // pourrait de toute façon pas être partagé avec les autres postes.
  useEffect(() => {
    if (!actif || !enLigne) return;

    void charger();
    const intervalle = setInterval(
      () => void charger(),
      visible ? DELAI_SONDAGE_FENETRE_OUVERTE_MS : DELAI_SONDAGE_REPOS_MS,
    );
    return () => clearInterval(intervalle);
  }, [actif, enLigne, visible, charger]);

  // ⚠️ Le retour du réseau ne lève PLUS la mise en attente. `isOnline` rebascule à
  // vrai dès le premier ping réussi après trois échecs (~6 s) : au marché en 4G
  // instable, chaque battement rouvrait la fenêtre entre deux clients. La mise en
  // attente dure un temps décidé (REPOS_APRES_PLUS_TARD_MS), rien d'autre ne la
  // lève — sauf l'ouverture manuelle depuis le menu.
  useEffect(() => {
    if (!masqueeJusqua) return;
    const restant = masqueeJusqua - Date.now();
    if (restant <= 0) { setMasqueeJusqua(null); return; }
    const t = setTimeout(() => setMasqueeJusqua(null), restant);
    return () => clearTimeout(t);
  }, [masqueeJusqua]);

  // Décision d'ouverture. Elle n'OUVRE que : une fenêtre déjà ouverte ne se referme
  // pas parce que l'opérateur recommence à toucher l'écran — il est justement en
  // train de la lire. La fermeture est un clic, ou l'acquittement d'un autre poste.
  useEffect(() => {
    if (!actif) return;

    const decider = () => {
      if (visibleRef.current || masqueeJusqua) return;
      if (lignesAFaireRef.current.size === 0) return;

      // Hors ligne, on n'ouvre PAS : la liste connue peut dater de plusieurs heures,
      // et un autre poste a pu y aller entre-temps. Envoyer quelqu'un au frigo sur
      // une information périmée est pire que de se taire.
      if (!enLigneRef.current) return;

      const etat = usePOSStore.getState();
      const panier = etat.vendorId ? etat.carts[etat.vendorId] || [] : [];

      // ⚠️ On n'ouvre QUE sur l'onglet Caisse, panier vide, et quand plus RIEN
      // d'autre n'est ouvert à l'écran.
      //
      // L'onglet Préparation est exclu pour une raison précise : son écouteur de
      // douchette (`OrderPreparation`) n'est borné que par l'onglet visible et ne
      // lit pas `scanSuppressed`. Une fenêtre posée par-dessus laisserait un scan
      // marquer une ligne « préparée » derrière le voile, sans retour visuel —
      // l'incident douchette du 04/09/2026 retourné : la marchandise réputée en
      // sac alors qu'elle est encore en rayon.
      if (etat.activeWorkspaceTab !== 'pos') return;
      if (panier.length > 0 || etat.paymentInProgress || etat.scanSuppressed || etat.pesageEnCours) return;

      // Garde générique, et c'est le point important : énumérer les dialogues un
      // par un ne tient pas. Le retrait de commande, la clôture Z et l'historique
      // des ventes ne publient AUCUN drapeau — ce sont des états locaux — et le
      // prochain dialogue ajouté n'en publiera pas davantage. On regarde donc
      // l'écran lui-même : tous les dialogues du poste se posent en `fixed inset-0`.
      // Ce qui est déjà à l'écran passe avant nous, quel qu'il soit.
      if (document.querySelector('.fixed.inset-0')) return;

      if (Date.now() - derniereActiviteRef.current < DELAI_CALME_MS) return;

      setVisible(true);
    };

    const intervalle = setInterval(decider, DELAI_DECISION_MS);
    return () => clearInterval(intervalle);
  }, [actif, masqueeJusqua]);

  const ouvrir = useCallback(() => {
    setErreur(null);
    setMasqueeJusqua(null);
    setDernierAcquittement(null);
    setVisible(true);
  }, []);

  const plusTard = useCallback(() => {
    setMasqueeJusqua(Date.now() + REPOS_APRES_PLUS_TARD_MS);
    setVisible(false);
  }, []);

  const oublierDernierAcquittement = useCallback(() => setDernierAcquittement(null), []);

  const rappelerDemain = useCallback(async (idsLus: string[]) => {
    if (!idsLus.length) return;
    setAcquittementEnCours(true);
    setErreur(null);
    try {
      const r = await reporterMisesDeCote(idsLus);
      setVisible(false);
      // Ce qui reste (retrait du jour) rouvrira la fenêtre : pas dans la minute.
      if (r.gardees > 0) setMasqueeJusqua(Date.now() + REPOS_APRES_PLUS_TARD_MS);
      await charger();
    } catch (err) {
      logger.warn('[MisesDeCote] report refusé:', err);
      setErreur("Le report n'est pas parti — réessayez, ou utilisez « Pas maintenant ».");
    } finally {
      setAcquittementEnCours(false);
    }
  }, [charger]);

  const articlesAFaire = tousLesArticles(commandes);

  /**
   * ⚠️ On acquitte ce que l'opérateur a LU — les articles rendus à l'écran — et
   * jamais `lignesAFaireRef`, que le sondage réécrit toutes les 15 secondes
   * pendant que la fenêtre est ouverte. Mesuré : 3 commandes sur 11 arrivent en
   * journée. Sans cela, partir trois minutes au frigo et revenir cliquer
   * acquittait aussi les articles d'une commande arrivée entre-temps, que
   * personne n'a jamais vus — l'inverse exact de ce que le bouton promet.
   */
  const acquitter = useCallback(async (idsLus?: string[]) => {
    const ids = idsLus && idsLus.length ? idsLus : Array.from(lignesAFaireRef.current);
    if (ids.length === 0) {
      setVisible(false);
      return;
    }

    setAcquittementEnCours(true);
    setErreur(null);
    try {
      const resultat = await acquitterMisesDeCote(ids);
      setDernierAcquittement({
        par_nom: resultat.par_nom ?? usePOSStore.getState().currentVendorName ?? null,
        at: resultat.at ?? new Date().toISOString(),
      });
      if (resultat.deja_acquittees) {
        logger.log(
          `[MisesDeCote] ${resultat.deja_acquittees} ligne(s) l'étaient déjà par un autre poste`,
        );
      }
      setVisible(false);
      // On repart de l'état du serveur : entre-temps une autre commande a pu entrer,
      // et c'est LUI qui fait foi sur qui a acquitté quoi.
      await charger();
    } catch (err) {
      // Ne JAMAIS refermer sur un échec : l'acquittement vaut pour les trois postes,
      // une fermeture optimiste laisserait les deux autres réclamer dans le vide.
      logger.warn('[MisesDeCote] acquittement refusé:', err);
      setErreur("L'acquittement n'est pas parti — les autres postes ne sont pas prévenus. Réessayez.");
    } finally {
      setAcquittementEnCours(false);
    }
  }, [charger]);

  return {
    commandes,
    articlesAFaire,
    nombreAFaire: articlesAFaire.length,
    visible,
    enLigne,
    acquittementEnCours,
    erreur,
    dernierAcquittement,
    ouvrir,
    acquitter,
    plusTard,
    rappelerDemain,
    oublierDernierAcquittement,
  };
}
